<script lang="ts">
	import './bannerNotification.component.css';
	import { onMount } from 'svelte';
	import {
		activeBanner,
		bannerStore,
		bannerDisplayInfos,
		bannerTypeLabels
	} from '../../../../stores/banner.store';
	import { renderBannerContent } from '../../../../utils/BannerContent.utils';
	import Modal from '../../../../__components__/modal/Modal.component.svelte';
	import type { ModalAction } from '../../../../interfaces/ModalAction';
	import { VpIconButton } from '@vtmn-play/svelte/VpIconButton';
	import { VpBadge } from '@vtmn-play/svelte/VpBadge';
	import { VpButton } from '@vtmn-play/svelte/VpButton';
	import { VpIcon } from '@vtmn-play/svelte/VpIcon';
	import { _ } from '../../../../utility-services/Translation.service';
	import { UserRoleEnum } from '../../../../interfaces/UserRoleEnum';
	import { userHasAnyRole } from '../../../shared/utils';

	const MESSAGE_PREVIEW_LIMIT = 180;

	let showDropdown = $state(false);
	let showDetails = $state(false);
	let container: HTMLElement | undefined = $state();
	let dropdownElement: HTMLElement | undefined = $state();
	let hasLongMessage = $derived(
		Boolean($activeBanner && $activeBanner.messagePrincipal.length > MESSAGE_PREVIEW_LIMIT)
	);
	let previewMessage = $derived(
		$activeBanner ? truncateMessage($activeBanner.messagePrincipal) : ''
	);
	let detailsModalButtons: ModalAction[] = $derived([
		{
			title: $_('close'),
			variant: 'secondary',
			testId: 'banner-notification-modal-close-bottom',
			action: closeDetails
		}
	]);

	onMount(async () => {
		const isAdmin = await userHasAnyRole([UserRoleEnum.Admin]);
		bannerStore.fetchBanners(isAdmin).catch(() => undefined);
	});

	function toggleDropdown() {
		showDropdown = !showDropdown;
	}

	function closeDropdown() {
		showDropdown = false;
	}

	function truncateMessage(message: string): string {
		if (message.length <= MESSAGE_PREVIEW_LIMIT) return message;
		return `${message.slice(0, MESSAGE_PREVIEW_LIMIT).trimEnd()}...`;
	}

	function openDetails() {
		showDropdown = false;
		showDetails = true;
	}

	function closeDetails() {
		showDetails = false;
	}

	function isInsideDropdown(target: EventTarget | null): boolean {
		if (!(target instanceof Node)) return false;
		return Boolean(container?.contains(target) || dropdownElement?.contains(target));
	}

	function handleDocumentClick(event: MouseEvent) {
		if (!isInsideDropdown(event.target)) {
			showDropdown = false;
		}
	}
</script>

<svelte:window onclick={handleDocumentClick} />

<div bind:this={container} class="vtmn-relative banner-notification">
	<span class="vtmn-relative banner-notification_button-wrapper">
		<VpIconButton
			variant="ghost"
			size="medium"
			altText={$_('banner_notification_access')}
			data-testid="banner-notification-button"
			onclick={toggleDropdown}
		>
			<VpIcon name="notification" size={20} />
		</VpIconButton>
		{#if $activeBanner}
			<VpBadge
				variant="important"
				position="top-right"
				aria-label="1 notification"
				data-testid="banner-notification-badge"
			>
				1
			</VpBadge>
		{/if}
	</span>

	{#if showDropdown && $activeBanner}
		<div
			bind:this={dropdownElement}
			class="vtmn-dropdown vtmn-dropdown_items vtmn-text-content-primary vtmn-absolute vtmn-rounded vtmn-shadow-sm banner-notification_dropdown dropdown-positioned dropdownItems"
			role="menu"
			data-testid="banner-notification-dropdown"
		>
			<VpIconButton
				variant="ghost"
				size="small"
				class="banner-notification_header-close"
				altText={$_('close')}
				data-testid="banner-notification-close"
				onclick={closeDropdown}
			>
				<VpIcon name="close" size={16} />
			</VpIconButton>
			<div class="vtmn-flex vtmn-items-center banner-notification_header">
				<span
					class="{bannerDisplayInfos[$activeBanner.type].iconClass} vtmn-mr-2"
					aria-hidden="true"
				></span>
				<span class="vtmn-font-bold vtmn-typo_text-3"
					>{$_(bannerTypeLabels[$activeBanner.type])}</span
				>
			</div>
			<div class="vtmn-font-bold banner-notification_title">{$activeBanner.title}</div>
			<div class="banner-notification_message">
				{@html renderBannerContent(previewMessage)}
			</div>
			<div class="banner-notification_actions vtmn-flex vtmn-items-center vtmn-gap-2">
				{#if hasLongMessage}
					<VpButton
						variant="secondary"
						size="small"
						class="banner-notification_details-button"
						data-testid="banner-notification-details"
						onclick={openDetails}
					>
						{$_('banner_notification_view_details')}
					</VpButton>
				{/if}
				<VpButton
					variant="secondary"
					size="small"
					class="banner-notification_close-button"
					data-testid="banner-notification-close-bottom"
					onclick={closeDropdown}
				>
					{$_('close')}
				</VpButton>
			</div>
		</div>
	{/if}
</div>

<Modal
	bind:show={showDetails}
	title={$activeBanner?.title ?? ''}
	buttons={detailsModalButtons}
	modalContentClass="banner-notification-details-modal"
>
	{#if $activeBanner}
		<div data-testid="banner-notification-details-modal">
			<div class="vtmn-flex vtmn-items-center vtmn-mb-4">
				<span
					class="{bannerDisplayInfos[$activeBanner.type].iconClass} vtmn-mr-2"
					aria-hidden="true"
				></span>
				<span class="vtmn-font-bold vtmn-typo_text-2">
					{$_(bannerTypeLabels[$activeBanner.type])}
				</span>
			</div>
			<div class="banner-notification_details-message">
				{@html renderBannerContent($activeBanner.messagePrincipal)}
			</div>
		</div>
	{/if}
</Modal>

<style src="./bannerNotification.component.css"></style>
